import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import TrustBadges from "@/components/TrustBadges";
import Services from "@/components/Services";
import StarlinkSection from "@/components/StarlinkSection";
import WhyChooseUs from "@/components/WhyChooseUs";
import CustomerTypes from "@/components/CustomerTypes";
import ProcessTimeline from "@/components/ProcessTimeline";
import Testimonials from "@/components/Testimonials";
import BlogTeaser from "@/components/BlogTeaser";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import PushSubscribeBanner from "@/components/PushSubscribeBanner";
import LiveInstallCounter from "@/components/LiveInstallCounter";
import FocusedServiceBanner from "@/components/FocusedServiceBanner";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDomain } from "@/hooks/useDomain";
import {
  absoluteUrl,
  buildHreflangAlternates,
  buildOrganizationJsonLd,
  buildLocalBusinessJsonLd,
  OG_LOCALE_MAP,
  SITE_NAME,
} from "@/lib/seo";

const FALLBACK_META = {
  de: {
    title: "Mallorca Digital & Tech Services · Starlink, Smart Home, WLAN, KI",
    description:
      "Premium Tech-Service auf Mallorca für deutsche Villa-Besitzer, Streamer und Digital Nomads. Starlink-Installation, Mesh-WLAN, Smart Home, KI-Automation und persönlicher Support auf Deutsch.",
  },
  en: {
    title: "Mallorca Digital & Tech Services · Starlink, Smart Home, WiFi, AI",
    description:
      "Premium tech service on Mallorca for German villa owners, streamers and digital nomads. Starlink install, mesh WiFi, smart home, AI automation and personal support.",
  },
  es: {
    title: "Mallorca Digital & Tech Services · Starlink, Smart Home, WiFi, IA",
    description:
      "Servicio tecnológico premium en Mallorca para propietarios de villas, streamers y nómadas digitales alemanes. Instalación Starlink, WiFi mesh, smart home, IA y soporte.",
  },
};

const Landing = () => {
  const { locale } = useLanguage();
  const domain = useDomain();

  // Domain-specific SEO overrides; fall back to brand default if none configured.
  const title = domain.seo?.title?.[locale] || domain.seo?.title?.de || FALLBACK_META[locale].title;
  const description =
    domain.seo?.description?.[locale] ||
    domain.seo?.description?.de ||
    FALLBACK_META[locale].description;

  const path = "/";
  const canonicalUrl = absoluteUrl(path);
  const alternates = buildHreflangAlternates(path);
  const ogLocale = OG_LOCALE_MAP[locale] || OG_LOCALE_MAP.de;
  const orgJsonLd = buildOrganizationJsonLd();
  const localBusinessJsonLd = buildLocalBusinessJsonLd();
  const ogImageUrl = absoluteUrl(
    `/api/og.png?domain=${encodeURIComponent(domain.hostname)}&locale=${locale}`,
  );

  return (
    <div
      data-testid="landing-page"
      data-domain={domain.hostname}
      data-focused-service={domain.focusedServiceSlug || ""}
      className="min-h-screen bg-[#fdfbf7] text-neutral-900"
    >
      <Helmet>
        <html lang={locale} />
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {alternates.map((a) => (
          <link key={a.hrefLang} rel="alternate" hrefLang={a.hrefLang} href={a.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={domain.isMain ? SITE_NAME : domain.brandName} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd)}</script>
      </Helmet>
      <Navigation />
      <main>
        <Hero />
        {!domain.isMain && <FocusedServiceBanner />}
        <TrustBadges />
        <Services />
        <StarlinkSection />
        <LiveInstallCounter />
        <WhyChooseUs />
        <CustomerTypes />
        <ProcessTimeline />
        <Testimonials />
        <BlogTeaser />
        <Contact />
      </main>
      <Footer />
      <WhatsAppButton />
      <PushSubscribeBanner />
    </div>
  );
};

export default Landing;
